// Turning an arrangement into something schedulable.
//
// The two views have genuinely different audio available, and pretending
// otherwise would silently produce a wrong mix:
//
//   ROOM  — every canonical clip carries its own R2 `audioKey` and a beat
//           position, so each clip is scheduled where it sits on the timeline.
//   SEED  — snapshot clips carry `filePath`, an absolute path on the producer's
//           machine, which is meaningless here. What a seed does have is one
//           full-length rendered stem per track, so each lane plays a single
//           source pinned at 0. The renders already contain the silence between
//           clips, so the result is the same arrangement.

import { applyMidiStages, type MidiStage } from "@web-shared/lib/midi-effects";
import { objectUrl } from "@web-shared/lib/api";
import { isLooped, objectSegments, objectStart } from "@web-shared/lib/clip-loop";
import { fileSecondsAt, tempoRatio, WARP_MODE_REPITCH, type WarpMarker } from "@web-shared/lib/warp";
import { WARP_MODE_COMPLEX, gatesAtOwnTempo, warpModeOf } from "@web-shared/lib/stretch/render";
import {
  beatsToSeconds,
  type ClipSource,
  type MidiNoteEvent,
  type MidiSource,
} from "@web-shared/lib/audio-engine";
import { notesDigest, type WireNote } from "@shared/midi/notes.js";
import { clipSounding, soundsAsAudio, soundsAsSynth } from "@shared/playback/geometry.js";
import { resolveNote } from "@web-shared/lib/piano-roll";
import type { Arrangement, ClipGeometry, Lane, LaneClip } from "@web-shared/lib/arrangement";
import type { SeedDetail } from "@web-shared/lib/types";
import { type WarpSettingsData } from "@shared/sync/protocol.js";

/** Room: one source per clip, positioned by beat. */
export function roomSources(arrangement: Arrangement, bpm: number): ClipSource[] {
  return clipSources(arrangement, bpm);
}

/** Like `roomSources`, but resolves each clip's audio key through a custom
 * URL map instead of `objectUrl`. For local mode where presigned URLs come
 * from the seed API rather than the tokenized R2 endpoint. */
export function roomSourcesWithUrls(
  arrangement: Arrangement,
  bpm: number,
  urlOf: (audioKey: string) => string | undefined,
): ClipSource[] {
  const out: ClipSource[] = [];
  for (const lane of arrangement.lanes) {
    for (const clip of lane.clips) {
      if (!clip.audioKey || !soundsAsAudio(clip)) continue;
      const url = urlOf(clip.audioKey);
      if (!url) continue;
      out.push(...clipSegments(lane, clip, url, bpm));
    }
  }
  return out;
}

/**
 * One clip's object as the transport schedules it: one source per playback
 * segment (`objectSegments`), so a looped clip repeats its loop the way Live
 * plays it and a plain one is a single source from the object's top. The
 * first segment keeps the clip's id; later ones are suffixed.
 */
/**
 * One audio clip's sources as the transport plays it — a source per loop
 * pass, with its warp and pitch — but timed from the clip's own start
 * rather than the timeline's: what a clip bounce renders.
 */
export function clipSourcesFromStart(lane: Lane, clip: LaneClip, url: string, bpm: number): ClipSource[] {
  return clipSegments(lane, { ...clip, start: 0 }, url, bpm);
}

function clipSegments(lane: Lane, clip: LaneClip, url: string, bpm: number): ClipSource[] {
  const geometry = clip.geometry;
  const timing = objectTiming(geometry, bpm);
  if (!geometry || !isLooped(geometry)) {
    // A warped clip plays its file from the start marker; an unwarped
    // one-shot (and a clip with no geometry) from the object's top.
    const head = geometry ? objectStart(geometry) : 0;
    return [
      {
        id: clip.id,
        laneId: lane.id,
        url,
        startSec: beatsToSeconds(clip.start, bpm),
        ...(head > 0 ? { offsetSec: beatsToSeconds(head, timing.objectBpm) } : {}),
        durationSec: beatsToSeconds(clip.duration, bpm),
        ...warpOf(timing),
      },
    ];
  }
  return objectSegments({ ...geometry, duration: clip.duration }).map((seg, i) => ({
    id: i === 0 ? clip.id : `${clip.id}#${i}`,
    laneId: lane.id,
    url,
    startSec: beatsToSeconds(clip.start + seg.atBeat, bpm),
    offsetSec: beatsToSeconds(seg.objectOffsetBeats, timing.objectBpm),
    durationSec: beatsToSeconds(seg.lengthBeats, bpm),
    ...warpOf(timing),
  }));
}

/** What a clip's object timing is measured in (slice 48). */
export interface ObjectTiming {
  /** The tempo the object's beats are seconds at: a warped clip's source
   * tempo when the wire knows it, else the room's (the object plays at
   * its own speed, as every clip did before). */
  objectBpm: number;
  /** Room tempo ÷ object tempo — the speed factor; 1 when nothing to do. */
  ratio: number;
  /** Live's Re-Pitch mode: the speed change moves the pitch. */
  repitch: boolean;
  /** Live's warp mode value (Complex when the clip names none): which
   * algorithm renders a warped clip — Beats, Complex, Complex Pro… */
  warpMode: number;
  /** The mode's knobs, when the clip set any (Beats and Complex Pro read them). */
  warpSettings?: WarpSettingsData;
  /** The room's tempo: the grid Beats' Preserve divisions are measured on. */
  gridBpm: number;
  /** Live's warp markers (slice 49): the engine plays the object through
   * them onto the room's grid, so every offset here is already in room
   * seconds (`objectBpm` is the room's). */
  warpMap?: { markers: readonly WarpMarker[]; bpm: number; fallbackBpm: number };
  /** The clip's Transpose + Detune, in semitones (0 when none). */
  semitones: number;
  /** An unwarped clip's transpose is a playback rate (vinyl), as in Live. */
  pitchByRate: boolean;
}

/** What `objectTiming` reads off a clip. */
export interface TimingGeometry {
  isWarped: boolean;
  sourceTempo?: number;
  warpMode?: number;
  warpMarkers?: readonly WarpMarker[];
  transpose?: number;
  detune?: number;
  warpSettings?: WarpSettingsData;
}

/**
 * How a clip's object relates to the room's tempo. Only a WARPED clip
 * with a known source tempo (or Live's markers) is ever stretched: an
 * unwarped one-shot plays at its file's speed whatever the tempo, exactly
 * as Live plays it.
 */
export function objectTiming(g: TimingGeometry | undefined, bpm: number): ObjectTiming {
  const semitones = (g?.transpose ?? 0) + (g?.detune ?? 0) / 100;
  if (!g || !g.isWarped) return { objectBpm: bpm, ratio: 1, repitch: false, warpMode: WARP_MODE_COMPLEX, gridBpm: bpm, semitones, pitchByRate: true };
  // Re-Pitch greys Live's Transpose knob out: speed alone moves the pitch.
  const warpMode = warpModeOf(g.warpMode);
  const repitch = warpMode === WARP_MODE_REPITCH;
  const pitch = { semitones: repitch ? 0 : semitones, pitchByRate: false };
  const knobs = g.warpSettings && Object.keys(g.warpSettings).length > 0 ? { warpSettings: g.warpSettings } : {};
  if (g.warpMarkers && g.warpMarkers.length >= 2 && bpm > 0) {
    return { objectBpm: bpm, ratio: 1, repitch, warpMode, ...knobs, gridBpm: bpm, warpMap: { markers: g.warpMarkers, bpm, fallbackBpm: g.sourceTempo ?? bpm }, ...pitch };
  }
  if (g.sourceTempo === undefined) return { objectBpm: bpm, ratio: 1, repitch, warpMode, ...knobs, gridBpm: bpm, ...pitch };
  const ratio = tempoRatio(bpm, g.sourceTempo);
  return { objectBpm: ratio === 1 ? bpm : g.sourceTempo, ratio, repitch, warpMode, ...knobs, gridBpm: bpm, ...pitch };
}

/**
 * How one clip's OBJECT sounds over `durationBeats`, played from its own top:
 * where in the object to start, how long, the loop brace when Live repeats
 * one, and the warp fields that put it on `bpm`'s grid.
 *
 * A warped clip's markers are file positions, so the head starts at the start
 * marker and a looped clip repeats its brace after it, the way Live's Session
 * View does; an unwarped one-shot plays from the object's top. This is the one
 * statement of it — `clipAudition` (the grid's click-to-play) and the clip
 * library's cards both go through here, so the two can never disagree about
 * what a card plays versus what the grid plays.
 */
export function objectPlayback(
  g: ClipGeometry | undefined,
  durationBeats: number,
  bpm: number,
): Pick<
  ClipSource,
  "offsetSec" | "durationSec" | "loopStartSec" | "loopEndSec" | "tempoRatio" | "repitch" | "warpMode" | "warpSettings" | "gridBpm" | "warpMap"
> {
  const head = g ? objectStart(g) : 0;
  const timing = objectTiming(g, bpm);
  return {
    ...(head > 0 ? { offsetSec: beatsToSeconds(head, timing.objectBpm) } : {}),
    durationSec: beatsToSeconds(durationBeats, bpm),
    ...(g && isLooped(g)
      ? {
          loopStartSec: beatsToSeconds(Math.max(0, g.loopStart), timing.objectBpm),
          loopEndSec: beatsToSeconds(Math.max(0, g.loopEnd), timing.objectBpm),
        }
      : {}),
    ...warpOf(timing),
  };
}

/** The source fields that make the engine play at the room's tempo. The
 * mode rides along only when it changes the render: Re-Pitch is `repitch`,
 * Complex is the default, so Beats / Complex Pro / Tones / Texture. */
export function warpOf(timing: ObjectTiming): Pick<ClipSource, "tempoRatio" | "repitch" | "warpMode" | "warpSettings" | "gridBpm" | "warpMap" | "semitones" | "pitchByRate"> {
  const pitch = timing.semitones !== 0 ? { semitones: timing.semitones, ...(timing.pitchByRate ? { pitchByRate: true } : {}) } : {};
  // The knobs ride along only for the modes that read them (Beats, Complex Pro).
  const knobs = timing.warpSettings && (timing.warpMode === 0 || timing.warpMode === 6) ? { warpSettings: timing.warpSettings, gridBpm: timing.gridBpm } : {};
  const how = timing.repitch ? { repitch: true } : timing.warpMode !== WARP_MODE_COMPLEX ? { warpMode: timing.warpMode, ...knobs } : {};
  if (timing.warpMap) return { warpMap: timing.warpMap, ...how, ...pitch };
  // At the object's own tempo nothing stretches — unless the mode gates
  // the sound anyway (Beats' envelope), which needs the render.
  if (timing.ratio === 1) return gatesAtOwnTempo(timing.warpMode, timing.warpSettings) ? { ...how, ...pitch } : pitch;
  return { tempoRatio: timing.ratio, ...how, ...pitch };
}

/**
 * Where a span of clip beats sits in the object's FILE, in seconds — what
 * a waveform over the object draws. Through the markers when the clip has
 * them; at the source tempo otherwise; the room's for everything else.
 */
export function objectFileSpan(
  g: TimingGeometry | undefined,
  fromBeat: number,
  toBeat: number,
  bpm: number,
): { fromSec: number; toSec: number } {
  const timing = objectTiming(g, bpm);
  if (timing.warpMap) {
    const fb = timing.warpMap.fallbackBpm;
    return { fromSec: fileSecondsAt(timing.warpMap.markers, fromBeat, fb), toSec: fileSecondsAt(timing.warpMap.markers, toBeat, fb) };
  }
  return { fromSec: beatsToSeconds(fromBeat, timing.objectBpm), toSec: beatsToSeconds(toBeat, timing.objectBpm) };
}

/**
 * Seed: one source per stem, matched to its lane.
 *
 * Matched by NAME first and index only as a fallback — `stems.track_index` is
 * the track's index in the Live Set, which coincides with the snapshot's array
 * position today but is not the same quantity, and a silent mis-mapping would
 * put a kick on the hi-hat's lane.
 */
export function seedSources(
  arrangement: Arrangement,
  detail: SeedDetail,
  bpm?: number,
): { sources: ClipSource[]; unmatched: string[] } {
  // Seeds that ship per-clip audio need none of the stem machinery below: each
  // clip owns an object and is scheduled at its own beat, exactly as a room
  // clip is. Chosen on the DATA, not on the channel — beta seeds uploaded
  // before per-clip audio still carry stems and must keep working.
  // Asked of the clips, not of the sources: a seed whose every clip is
  // deactivated is still a per-clip seed, and silent — never its stems.
  // Asked of AUDIO clips only: a MIDI clip's bounce is not per-clip audio
  // for the Set, and a MIDI-only seed with bounces still has stems to play.
  if (arrangement.lanes.some((lane) => lane.kind !== "midi" && lane.clips.some((c) => Boolean(c.audioKey)))) {
    return { sources: clipSources(arrangement, bpm ?? 120), unmatched: [] };
  }

  // Stems are track renders of AUDIO tracks. A MIDI lane plays its notes
  // (`midiSources`); a stem landing on it would sound under the synth.
  const audioLanes = arrangement.lanes.filter((lane) => lane.kind !== "midi");
  const byName = new Map<string, string>();
  audioLanes.forEach((lane) => byName.set(lane.name, lane.id));

  const sources: ClipSource[] = [];
  const unmatched: string[] = [];

  for (const stem of detail.stems) {
    const laneId = byName.get(stem.name) ?? audioLanes[stem.trackIndex]?.id;
    if (!laneId) {
      unmatched.push(stem.name);
      continue;
    }
    sources.push({
      id: stem.id,
      laneId,
      url: objectUrl(stem.key),
      startSec: 0,
      // No duration: a stem is a full-length track render, and its own length
      // is the extent. The engine reads that off the decoded buffer.
    });
  }
  return { sources, unmatched };
}

/**
 * One source per clip, for a seed whose clips carry their own audio.
 *
 * Identical in shape to `roomSources` — which is the point: a seed in this form
 * and a live room describe audio the same way, so they schedule the same way.
 * A deactivated clip (`muted`, Live's clip activator off) is drawn but yields
 * no source — it is silent in Live, so it is silent here.
 */
function clipSources(arrangement: Arrangement, bpm: number): ClipSource[] {
  const out: ClipSource[] = [];
  for (const lane of arrangement.lanes) {
    for (const clip of lane.clips) {
      // No bytes for this clip (it plays as a gap), deactivated, or a MIDI
      // clip that sounds through the synth instead — `clipSounding` is the
      // one word on which of the two a clip is.
      if (!clip.audioKey || !soundsAsAudio(clip)) continue;
      out.push(...clipSegments(lane, clip, objectUrl(clip.audioKey), bpm));
    }
  }
  return out;
}

/**
 * MIDI clips → schedulable note lists, for BOTH views. Rooms and seeds already
 * agree on the shape: flattened notes in clip-relative beats on the lane clip
 * (shared/midi/notes.ts), so one builder serves the two pages.
 *
 * Muted notes are dropped here — they exist on the wire only so an edit that
 * un-mutes one round-trips; they never sound. A clip whose notes were omitted
 * for size (`notesOmitted`) yields no source at all: silence is honest, a
 * partial clip would not be.
 */
export function midiSources(arrangement: Arrangement, bpm: number, effectsByLane: Record<string, readonly MidiStage[]> = {}): MidiSource[] {
  const out: MidiSource[] = [];
  for (const lane of arrangement.lanes) {
    for (const clip of lane.clips) {
      if (clip.muted) continue; // deactivated: drawn, never scheduled
      const src = midiSourceOf(lane, clip, bpm, effectsByLane[lane.id] ?? []);
      if (src) out.push(src);
    }
  }
  return out;
}

/** The audio-clip `clipAudition` counterpart: one MIDI clip for click-to-play,
 * played from its own start. Null when there is nothing audible in it. */
export function midiClipAudition(lane: Lane, clip: LaneClip, bpm: number, effects: readonly MidiStage[] = []): MidiSource | null {
  const src = midiSourceOf(lane, clip, bpm, effects);
  if (!src) return null;
  return { ...src, id: `audition:${clip.id}`, startSec: 0 };
}

/**
 * Wire notes → a schedulable MidiSource, at the caller's placement. The one
 * spot where "what sounds" is decided for MIDI: muted notes dropped, an empty
 * or all-muted list is null. Used by the arrangement path above and by the
 * clip library, so the two tabs can never disagree about audibility.
 */
export function midiNotesToSource(
  notes: readonly WireNote[] | undefined,
  bpm: number,
  placement: { id: string; laneId: string; startSec: number; durationSec: number },
  rng: () => number = Math.random,
  /** The lane's MIDI effects (midi-effects.ts), applied after chance and
   * deviation roll — the order Live's chain runs them. */
  effects: readonly MidiStage[] = [],
): MidiSource | null {
  if (!notes || notes.length === 0) return null;
  const rolled: WireNote[] = [];
  for (const raw of notes) {
    if (raw.muted) continue;
    // Chance and velocity deviation roll here, once per schedule — so each
    // pass of a launched loop and each mixdown rolls afresh, as Live's do.
    const n = resolveNote(raw, rng);
    if (n) rolled.push(n);
  }
  const shaped = effects.length > 0 ? applyMidiStages(rolled, effects, rng, bpm > 0 ? (placement.durationSec * bpm) / 60 : Infinity, bpm > 0 ? (placement.startSec * bpm) / 60 : 0) : rolled;
  const events: MidiNoteEvent[] = [];
  for (const n of shaped) {
    events.push({
      pitch: n.pitch,
      startSec: beatsToSeconds(n.startTime, bpm),
      durationSec: beatsToSeconds(n.duration, bpm),
      velocity: n.velocity,
    });
  }
  const rolls =
    notes.some((n) => !n.muted && ((n.probability !== undefined && n.probability < 1) || (n.velocityDeviation ?? 0) !== 0)) ||
    (rolled.length > 0 && effects.some((e) => e.kind === "random"));
  // Nothing sounded and nothing could: no source. A roll that MISSED is
  // still a source (silent this time, keyed by its material), so a rebuild
  // per broadcast neither adds nor drops an entry and never reschedules.
  if (events.length === 0 && !rolls) return null;
  return {
    ...placement,
    notes: events,
    // What this was made from — a rebuild on the next broadcast rolls chance
    // again, and that must not read as a change of material.
    key: `${notesDigest(notes)}|${bpm}|${effects.length > 0 ? JSON.stringify(effects) : ""}`,
    ...(rolls ? { reroll: () => midiNotesToSource(notes, bpm, placement, rng, effects)?.notes ?? [] } : {}),
  };
}

function midiSourceOf(lane: Lane, clip: LaneClip, bpm: number, effects: readonly MidiStage[] = []): MidiSource | null {
  // A MIDI clip with its Live bounce is heard as that bounce (`clipSources`),
  // never as the synth on top of it.
  if (!soundsAsSynth(clip)) return null;
  return midiNotesToSource(
    clip.notes,
    bpm,
    {
      id: clip.id,
      laneId: lane.id,
      startSec: beatsToSeconds(clip.start, bpm),
      durationSec: beatsToSeconds(clip.duration, bpm),
    },
    Math.random,
    effects,
  );
}

/** Master mixdown: a single source, when there are no stems to lay out. */
export function masterSource(detail: SeedDetail): ClipSource[] {
  if (!detail.masterKey) return [];
  return [
    {
      id: "master",
      laneId: "master",
      url: objectUrl(detail.masterKey),
      startSec: 0,
    },
  ];
}

// ---- Tempo selection --------------------------------------------------------

export type TempoSource = "room" | "seed" | "default";

/**
 * Which tempo to schedule an arrangement at.
 *
 * Clip positions are in BEATS, so the wrong tempo stretches the entire
 * arrangement rather than failing visibly. A room's `tempo` is only meaningful
 * when `tempoRev > 0`: rev 0 means no client ever asserted one, so the value is
 * the Durable Object's `emptyRoom(120)` placeholder (shared/sync/state.ts). The
 * seed the room was opened from knows the real tempo, so prefer it in that case
 * — a 128 BPM Set played at the placeholder 120 runs 6.7% slow.
 */
export function effectiveTempo(
  room: { tempo: number; tempoRev: number } | null | undefined,
  seedTempo: number | null | undefined,
): { tempo: number; source: TempoSource } {
  if (room && room.tempoRev > 0 && room.tempo > 0) {
    return { tempo: room.tempo, source: "room" };
  }
  if (seedTempo && seedTempo > 0) return { tempo: seedTempo, source: "seed" };
  if (room && room.tempo > 0) return { tempo: room.tempo, source: "default" };
  return { tempo: 120, source: "default" };
}

// ---- Auditioning one clip ---------------------------------------------------

/**
 * The audio for a single clip, for click-to-play.
 *
 * The two views need genuinely different lookups again. A room clip owns its
 * bytes, so it plays from the top of its own object. A seed clip owns nothing —
 * but its track's stem is a full-length render on the same timeline, so the clip
 * is simply the window `[start, start + duration]` seconds into that stem. That
 * equivalence is the whole reason a seed's clips can be auditioned at all.
 *
 * Returns null when there is nothing to play, so the caller can leave the clip
 * inert rather than offering a button that does nothing.
 */
export function clipAudition(
  lane: Lane,
  clip: LaneClip,
  bpm: number,
  stemUrlForLane?: (laneId: string) => string | undefined,
): ClipSource | null {
  if (clip.audioKey) {
    return {
      id: `audition:${clip.id}`,
      laneId: lane.id,
      url: objectUrl(clip.audioKey),
      startSec: 0,
      ...objectPlayback(clip.geometry, clip.duration, bpm),
    };
  }

  const stem = stemUrlForLane?.(lane.id);
  if (!stem) return null;
  return {
    id: `audition:${clip.id}`,
    laneId: lane.id,
    url: stem,
    startSec: 0,
    offsetSec: beatsToSeconds(clip.start, bpm),
    durationSec: beatsToSeconds(clip.duration, bpm),
  };
}

/**
 * Click-to-play for ANY clip: its bounce or object as audio, its notes as
 * synth, by `clipSounding` — the same word the transport goes by, so a pad,
 * a tap and the timeline never disagree about what a clip is.
 */
export function clipAuditionSource(
  lane: Lane,
  clip: LaneClip,
  bpm: number,
  options: { effects?: readonly MidiStage[]; stemUrlForLane?: (laneId: string) => string | undefined } = {},
): ClipSource | MidiSource | null {
  switch (clipSounding(clip).kind) {
    case "synth":
      return midiClipAudition(lane, clip, bpm, options.effects);
    case "audio":
      return clipAudition(lane, clip, bpm, options.stemUrlForLane);
    default:
      // A deactivated clip is silent in Live however it is started; a seed
      // audio clip with no object of its own still has its lane's stem.
      return clip.muted || clip.kind === "midi" ? null : clipAudition(lane, clip, bpm, options.stemUrlForLane);
  }
}

/** Lane id → stem URL, for the seed page's audition lookup. */
export function stemUrlsByLane(
  arrangement: Arrangement,
  detail: SeedDetail,
): Map<string, string> {
  const { sources } = seedSources(arrangement, detail);
  return new Map(sources.map((s) => [s.laneId, s.url]));
}
